import { useId, useState, type ReactNode } from 'react';
import { BarChart3, Table2 } from 'lucide-react';

interface ChartCardProps {
  title: string;
  subtitle?: string;
  chart: ReactNode;
  /** The same figures as a table. Every chart has one. */
  table: ReactNode;
  /** When true there is nothing to draw, and `emptyMessage` says so. */
  empty?: boolean;
  emptyMessage?: string;
  className?: string;
}

type View = 'chart' | 'table';

/**
 * A chart, with a switch to the table of the same figures: for who reads a
 * table better, cannot tell the colours apart, or uses a screen reader.
 */
export default function ChartCard({
  title,
  subtitle,
  chart,
  table,
  empty = false,
  emptyMessage = 'Sem dados no período escolhido.',
  className = '',
}: ChartCardProps) {
  const [view, setView] = useState<View>('chart');
  const headingId = useId();

  return (
    <section className={`card p-5 ${className}`} aria-labelledby={headingId}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h2 id={headingId} className="text-sm font-semibold text-foreground">
            {title}
          </h2>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {!empty && (
          <div className="flex shrink-0 rounded-md border border-border overflow-hidden" role="group" aria-label={`Ver ${title} como`}>
            <ViewButton view="chart" current={view} onSelect={setView} label="Gráfico" icon={BarChart3} />
            <ViewButton view="table" current={view} onSelect={setView} label="Tabela" icon={Table2} />
          </div>
        )}
      </div>

      {empty ? (
        <p className="text-sm text-muted-foreground py-8 text-center">{emptyMessage}</p>
      ) : view === 'chart' ? (
        chart
      ) : (
        <div className="overflow-x-auto max-h-80 overflow-y-auto">{table}</div>
      )}
    </section>
  );
}

interface ViewButtonProps {
  view: View;
  current: View;
  onSelect: (view: View) => void;
  label: string;
  icon: React.ElementType;
}

function ViewButton({ view, current, onSelect, label, icon: Icon }: ViewButtonProps) {
  const pressed = view === current;
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={() => onSelect(view)}
      className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-colors ${
        pressed ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent'
      }`}
    >
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      {label}
    </button>
  );
}
